import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { flowFetch, FlowUser } from './flowApi';
import { MentionField, resolveMentions } from './MentionField';

interface ChatChannel {
  id: string; type: 'DIRECT' | 'GROUP' | 'AREA'; name: string;
  subtitle?: string | null; color?: string | null; areaId?: string | null;
  lastMessage?: { content: string; at: string; authorName: string } | null;
  lastAt: string; unread: number;
}
interface ChatMessage {
  id: string; content: string; authorId: string; createdAt: string;
  author?: FlowUser | null; mine: boolean;
}

function initials(name?: string) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[1]?.[0] || '')).toUpperCase();
}
function timeStr(v: string) {
  const d = new Date(v);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function ChannelAvatar({ ch }: { ch: ChatChannel }) {
  if (ch.type === 'AREA') {
    return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black text-white" style={{ background: ch.color || '#0ea5e9' }}>{ch.name.charAt(0).toUpperCase()}</span>;
  }
  if (ch.type === 'GROUP') {
    return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg></span>;
  }
  return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-700">{initials(ch.name)}</span>;
}

// Modal "Nova conversa"
function NewChatModal({ users, onClose, onOpened }: { users: FlowUser[]; onClose: () => void; onOpened: (channelId: string) => void }) {
  const [mode, setMode] = useState<'direct' | 'group'>('direct');
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const startDirect = async (userId: string) => {
    setBusy(true);
    try {
      const data = await flowFetch('/api/flow/chat/direct', { method: 'POST', body: JSON.stringify({ userId }) });
      onOpened(data.channelId);
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); } finally { setBusy(false); }
  };
  const createGroup = async () => {
    if (!groupName.trim()) { toast.error('Informe o nome do grupo'); return; }
    setBusy(true);
    try {
      const data = await flowFetch('/api/flow/chat/groups', { method: 'POST', body: JSON.stringify({ name: groupName.trim(), memberIds: selected }) });
      onOpened(data.channelId);
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-900">Nova conversa</h2>
          <div className="mt-3 inline-flex gap-1 rounded-xl bg-slate-100 p-1">
            <button onClick={() => setMode('direct')} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${mode === 'direct' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Pessoa</button>
            <button onClick={() => setMode('group')} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${mode === 'group' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Grupo</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {mode === 'direct' ? (
            <div className="space-y-1">
              {users.map(u => (
                <button key={u.id} disabled={busy} onClick={() => startDirect(u.id)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-slate-50">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-100 text-xs font-bold text-cyan-700">{initials(u.nome)}</span>
                  <span className="text-sm font-medium text-slate-800">{u.nome}</span>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <input autoFocus value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Nome do grupo"
                className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Participantes</p>
              <div className="max-h-56 space-y-1 overflow-y-auto">
                {users.map(u => (
                  <label key={u.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                    <input type="checkbox" className="accent-cyan-600" checked={selected.includes(u.id)}
                      onChange={e => setSelected(e.target.checked ? [...selected, u.id] : selected.filter(id => id !== u.id))} />
                    {u.nome}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        {mode === 'group' && (
          <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4">
            <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">Cancelar</button>
            <button onClick={createGroup} disabled={busy} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">Criar grupo</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function FlowChat({ users }: { users: FlowUser[] }) {
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [channelInfo, setChannelInfo] = useState<any>(null);
  const [members, setMembers] = useState<FlowUser[]>([]);
  const [input, setInput] = useState('');
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const loadChannels = async () => {
    try {
      const data = await flowFetch('/api/flow/chat/channels');
      setChannels(data.channels);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar conversas');
    } finally {
      setLoadingChannels(false);
    }
  };

  const loadMessages = async (channelId: string, scroll = false) => {
    try {
      const data = await flowFetch(`/api/flow/chat/channels/${channelId}/messages`);
      setChannelInfo(data.channel);
      setMembers(data.members || []);
      setMessages(data.messages);
      if (scroll) setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar mensagens');
    }
  };

  useEffect(() => { loadChannels(); }, []);

  // polling: atualiza lista e mensagens do canal aberto
  useEffect(() => {
    const interval = setInterval(() => {
      loadChannels();
      if (selectedId) loadMessages(selectedId);
    }, 4000);
    return () => clearInterval(interval);
  }, [selectedId]);

  const openChannel = async (id: string) => {
    setSelectedId(id);
    setMessages([]);
    await loadMessages(id, true);
    loadChannels();
  };

  // Abre o canal indicado na URL (?c=<id>), vindo de uma notificação
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('c');
    if (c && c !== selectedId) openChannel(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const send = async () => {
    if (!input.trim() || !selectedId) return;
    const content = input.trim();
    const mentions = resolveMentions(content, members);
    setInput('');
    try {
      await flowFetch(`/api/flow/chat/channels/${selectedId}/messages`, { method: 'POST', body: JSON.stringify({ content, mentions }) });
      await loadMessages(selectedId, true);
      loadChannels();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar');
    }
  };

  const selectedChannel = channels.find(c => c.id === selectedId);

  // Apaga a conversa apenas para o usuario atual (nao afeta o outro participante).
  const deleteConversation = async () => {
    if (!selectedId || !selectedChannel || selectedChannel.type === 'AREA') return;
    if (!window.confirm('Apagar esta conversa? Ela será removida apenas para você — o outro participante continua com a conversa. Se ele te enviar uma nova mensagem, ela reaparece.')) return;
    try {
      await flowFetch(`/api/flow/chat/channels/${selectedId}`, { method: 'DELETE' });
      toast.success('Conversa apagada para você.');
      setSelectedId(null);
      setMessages([]);
      setChannelInfo(null);
      loadChannels();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao apagar conversa');
    }
  };

  return (
    <div className="flex h-[calc(100vh-9.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Lista de conversas */}
      <aside className={`flex w-full flex-col border-r border-slate-200 md:w-80 ${selectedId ? 'hidden md:flex' : 'flex'}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-bold text-slate-800">Conversas</h2>
          <button onClick={() => setShowNew(true)} className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700">+ Nova</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingChannels ? (
            <div className="flex justify-center py-10"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
          ) : channels.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400">Nenhuma conversa ainda. Clique em “+ Nova”.</p>
          ) : channels.map(ch => (
            <button key={ch.id} onClick={() => openChannel(ch.id)}
              className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 ${selectedId === ch.id ? 'bg-cyan-50/60' : ''}`}>
              <ChannelAvatar ch={ch} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-800">{ch.name}</span>
                  <span className="shrink-0 text-[10px] text-slate-400">{ch.lastMessage ? timeStr(ch.lastMessage.at) : ''}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-slate-500">
                    {ch.subtitle && <span className="mr-1 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-bold uppercase text-slate-500">{ch.subtitle}</span>}
                    {ch.lastMessage ? `${ch.lastMessage.authorName ? ch.lastMessage.authorName.split(' ')[0] + ': ' : ''}${ch.lastMessage.content}` : 'Sem mensagens'}
                  </span>
                  {ch.unread > 0 && <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-cyan-600 px-1.5 text-[10px] font-bold text-white">{ch.unread}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Thread */}
      <section className={`flex-1 flex-col ${selectedId ? 'flex' : 'hidden md:flex'}`}>
        {!selectedChannel ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">Selecione uma conversa para começar.</div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-slate-200 px-5 py-3">
              <button onClick={() => setSelectedId(null)} className="md:hidden text-slate-400">←</button>
              <ChannelAvatar ch={selectedChannel} />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-800">{channelInfo?.name || selectedChannel.name}</p>
                <p className="truncate text-xs text-slate-400">
                  {selectedChannel.type === 'DIRECT' ? 'Conversa direta' : `${members.length} participantes`}
                </p>
              </div>
              {selectedChannel.type !== 'AREA' && (
                <button
                  onClick={deleteConversation}
                  title="Apagar conversa (somente para você)"
                  aria-label="Apagar conversa"
                  className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </header>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-5">
              {messages.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">Nenhuma mensagem ainda. Diga olá! 👋</p>
              ) : messages.map(m => (
                <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${m.mine ? 'bg-cyan-600 text-white' : 'border border-slate-200 bg-white text-slate-900'}`}>
                    {!m.mine && selectedChannel.type !== 'DIRECT' && (
                      <div className="mb-0.5 text-[11px] font-semibold opacity-70">{m.author?.nome || '—'}</div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.content}</div>
                    <div className="mt-1 text-right text-[10px] opacity-70">{new Date(m.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
              ))}
            </div>

            <footer className="border-t border-slate-200 bg-white p-3">
              <div className="flex items-end gap-2">
                <MentionField value={input} onChange={setInput} users={members} onSubmit={send} multiline
                  placeholder="Escreva uma mensagem... digite @ para mencionar"
                  className="w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500" />
                <button onClick={send} disabled={!input.trim()}
                  className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50">Enviar</button>
              </div>
            </footer>
          </>
        )}
      </section>

      {showNew && (
        <NewChatModal users={users} onClose={() => setShowNew(false)} onOpened={(id) => { setShowNew(false); loadChannels(); openChannel(id); }} />
      )}
    </div>
  );
}
